/**
 * LocationBadge Component
 * 
 * Displays customer location and language information with geographic context.
 * Shows country, city, timezone, and language preferences in a clean badge format.
 * 
 * Features:
 * - Geographic location display with country flags
 * - Timezone and language information
 * - Hover tooltips with detailed location data
 * - IP address information for debugging (admin view)
 */

import React from 'react'
import * as Types from '../../../types'
type CustomerProfileType = Types.CustomerProfile
import { 
  MapPin, 
  Globe, 
  Clock,
  Languages,
  Info
} from 'lucide-react'

export interface LocationBadgeProps {
  /** Customer profile containing location information */
  profile: CustomerProfileType
  /** Size variant for different use cases */
  size?: 'sm' | 'md' | 'lg'
  /** Show detailed tooltip on hover */
  showTooltip?: boolean
  /** Show IP address (admin/debug mode) */
  showIpAddress?: boolean
  /** Custom CSS classes */
  className?: string
}

/**
 * Format location display text
 */
function formatLocationText(profile: CustomerProfileType): string {
  if (profile.city && profile.country) {
    return `${profile.city}, ${profile.country}`
  }
  
  if (profile.country) {
    return profile.country
  }
  
  return 'Unknown Location'
}

/**
 * Format language display
 */
function formatLanguage(languageCode?: string): string {
  if (!languageCode) return 'Unknown'
  
  try {
    // Extract primary language code (e.g., 'en' from 'en-US')
    const primaryLang = languageCode.split('-')[0]
    const displayNames = new Intl.DisplayNames(['en'], { type: 'language' })
    return displayNames.of(primaryLang) || languageCode
  } catch {
    return languageCode
  }
}

/**
 * Format timezone for display
 */
function formatTimezone(timezone?: string): string {
  if (!timezone) return 'Unknown'
  
  try {
    // Convert timezone to readable format
    const now = new Date()
    const timeInZone = now.toLocaleString('en-US', { 
      timeZone: timezone,
      timeZoneName: 'short'
    })
    
    // Extract timezone abbreviation
    const timezoneAbbr = timeInZone.split(' ').pop() || timezone
    return timezoneAbbr
  } catch {
    return timezone
  }
}

/**
 * Get country flag emoji (basic implementation)
 */
function getCountryFlag(country?: string): string {
  if (!country) return '🌍'
  
  // Common country mappings
  const flagMap: Record<string, string> = {
    'United States': '🇺🇸',
    'US': '🇺🇸',
    'USA': '🇺🇸',
    'Canada': '🇨🇦',
    'United Kingdom': '🇬🇧',
    'UK': '🇬🇧',
    'Germany': '🇩🇪',
    'France': '🇫🇷',
    'Spain': '🇪🇸',
    'Italy': '🇮🇹',
    'Japan': '🇯🇵',
    'Australia': '🇦🇺',
    'Brazil': '🇧🇷',
    'India': '🇮🇳',
    'China': '🇨🇳',
    'Russia': '🇷🇺',
    'Mexico': '🇲🇽',
    'Netherlands': '🇳🇱',
    'Sweden': '🇸🇪',
    'Norway': '🇳🇴',
    'Denmark': '🇩🇰',
    'Finland': '🇫🇮'
  }
  
  return flagMap[country] || '🌍'
}

/**
 * LocationBadge component for displaying customer location information
 */
export function LocationBadge({
  profile,
  size = 'md',
  showTooltip = true,
  showIpAddress = false,
  className = ''
}: LocationBadgeProps) {
  
  const locationText = formatLocationText(profile)
  const language = formatLanguage(profile.language)
  const timezone = formatTimezone(profile.timezone)
  const countryFlag = getCountryFlag(profile.country)
  
  // Size-based styling
  const sizeClasses = {
    sm: 'text-xs px-2 py-1 gap-1',
    md: 'text-sm px-3 py-1.5 gap-1.5',
    lg: 'text-base px-4 py-2 gap-2'
  }
  
  // Create detailed tooltip content
  const tooltipContent = showTooltip ? (
    <div className="space-y-1">
      <div className="font-medium">Location Information</div>
      <div className="text-xs space-y-0.5">
        <div><span className="font-medium">Location:</span> {locationText}</div>
        {profile.language && (
          <div><span className="font-medium">Language:</span> {language} ({profile.language})</div>
        )}
        {profile.timezone && (
          <div><span className="font-medium">Timezone:</span> {profile.timezone} ({timezone})</div>
        )}
        {showIpAddress && profile.ip_address && (
          <div><span className="font-medium">IP Address:</span> {profile.ip_address}</div>
        )}
      </div>
    </div>
  ) : null
  
  return (
    <div className="relative group">
      <div 
        className={`
          inline-flex items-center rounded-md bg-blue-50 text-blue-700
          border border-blue-200 font-medium
          ${sizeClasses[size]}
          ${className}
        `}
        title={showTooltip ? undefined : `${locationText} • ${language}`}
      >
        {/* Country flag or location icon */}
        <span className="text-blue-500">
          {countryFlag !== '🌍' ? (
            <span className="text-sm">{countryFlag}</span>
          ) : (
            <MapPin className="w-3 h-3" />
          )}
        </span>
        
        {/* Location and language information */}
        <span className="truncate">
          {locationText} • {language}
        </span>
        
        {/* Info icon for tooltip indicator */}
        {showTooltip && (
          <Info className="w-3 h-3 text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        )}
      </div>
      
      {/* Detailed tooltip on hover */}
      {showTooltip && tooltipContent && (
        <div className="
          absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2
          hidden group-hover:block z-50
          bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg
          min-w-max max-w-xs
          before:content-[''] before:absolute before:top-full before:left-1/2 
          before:transform before:-translate-x-1/2 before:border-4
          before:border-transparent before:border-t-gray-900
        ">
          {tooltipContent}
        </div>
      )}
    </div>
  )
}

/**
 * Compact location badge for small spaces
 */
export function CompactLocationBadge({ profile }: { profile: CustomerProfileType }) {
  const countryFlag = getCountryFlag(profile.country)
  const country = profile.country || 'Unknown'
  
  return (
    <div 
      className="inline-flex items-center gap-1 text-xs text-gray-600"
      title={formatLocationText(profile)}
    >
      <span className="text-sm">
        {countryFlag}
      </span>
      <span className="truncate max-w-16">
        {country}
      </span>
    </div>
  )
}

/**
 * Timezone indicator for real-time context
 */
export function TimezoneIndicator({ 
  timezone, 
  className = '' 
}: { 
  timezone?: string
  className?: string 
}) {
  const formattedTimezone = formatTimezone(timezone)
  
  if (!timezone) return null
  
  return (
    <div 
      className={`inline-flex items-center gap-1 text-xs text-gray-500 ${className}`}
      title={`Customer timezone: ${timezone}`}
    >
      <Clock className="w-3 h-3" />
      <span>{formattedTimezone}</span>
    </div>
  )
}

/**
 * Language indicator component
 */
export function LanguageIndicator({ 
  language, 
  className = '' 
}: { 
  language?: string
  className?: string 
}) {
  const formattedLanguage = formatLanguage(language)
  
  if (!language) return null
  
  return (
    <div 
      className={`inline-flex items-center gap-1 text-xs text-gray-500 ${className}`}
      title={`Customer language: ${language}`}
    >
      <Languages className="w-3 h-3" />
      <span>{formattedLanguage}</span>
    </div>
  )
}

export default LocationBadge